import React from "react";
import { Box, Typography, Divider } from "@mui/material";

const TrialDetails = ({ data }) => {
  const {
    briefTitle,
    officialTitle,
    organization,
    trialId,
    registryType,
    studyType,
    enrollmentInfo,
    briefSummary,
    overallStatus,
    primaryOutcomes,
    secondaryOutcomes,
    startDate,
    completionDate,
    conditions,
    keywords,
    interventions,
    arms,
  } = data;

  return (
    <Box sx={{ maxWidth: "1400px", margin: "2rem auto", padding: 2 }}>
      <Typography variant="h4" gutterBottom>
        {briefTitle}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {officialTitle}
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        <Box sx={{ flex: "1 1 45%" }}>
          <Typography variant="h6">Basic Information</Typography>
          <Typography variant="body2">
            <strong>Sponsor:</strong> {organization?.fullName}
          </Typography>
          <Typography variant="body2">
            <strong>Trial ID:</strong> {trialId}
          </Typography>
          <Typography variant="body2">
            <strong>Registry Type:</strong> {registryType}
          </Typography>
          <Typography variant="body2">
            <strong>Study Type:</strong> {studyType}
          </Typography>
          {conditions?.length > 0 && (
            <Typography variant="body2">
              <strong>Conditions:</strong> {conditions.join(", ")}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: "1 1 45%" }}>
          <Typography variant="h6">Status and Enrollment</Typography>
          <Box
            sx={{
              display: "inline-block",
              backgroundColor:
                overallStatus === "COMPLETED" ? "error.main" : "success.main",
              color: "white",
              padding: "4px 10px",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: "bold",
              mb: 1,
            }}
          >
            {overallStatus}
          </Box>
          <Typography variant="body2">
            <strong>Enrollment:</strong> {enrollmentInfo?.count} (
            {enrollmentInfo?.type})
          </Typography>
          <Typography variant="body2">
            <strong>Start Date:</strong>{" "}
            {startDate ? new Date(startDate).toLocaleDateString() : "N/A"}
          </Typography>
          <Typography variant="body2">
            <strong>Completion Date:</strong>{" "}
            {completionDate
              ? new Date(completionDate).toLocaleDateString()
              : "N/A"}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6">Brief Summary</Typography>
      <Typography variant="body1">{briefSummary}</Typography>

      <Divider sx={{ my: 2 }} />

      {keywords?.length > 0 && (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: 2 }}>
            <Box sx={{ flex: "1 1 45%" }}>
              <Typography variant="h6" gutterBottom>
                Keywords
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {keywords.join(", ")}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {(interventions?.length > 0 || arms?.length > 0) && (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {interventions?.length > 0 && (
              <Box sx={{ flex: "1 1 45%" }}>
                <Typography variant="h6" gutterBottom>
                  Interventions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {interventions.map((intervention, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Type:</strong> {intervention.type}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Name:</strong> {intervention.name}
                      </Typography>
                      {intervention.description && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {intervention.description}
                        </Typography>
                      )}
                      {intervention.armGroupLabels?.length > 0 && (
                        <Typography variant="body2">
                          <strong>Arm Groups:</strong>{" "}
                          {intervention.armGroupLabels.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {arms?.length > 0 && (
              <Box sx={{ flex: "1 1 45%" }}>
                <Typography variant="h6" gutterBottom>
                  Study Arms/Groups
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {arms.map((arm, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Label:</strong> {arm.label}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {arm.type}
                      </Typography>
                      {arm.description && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {arm.description}
                        </Typography>
                      )}
                      {arm.interventionNames?.length > 0 && (
                        <Typography variant="body2">
                          <strong>Interventions:</strong>{" "}
                          {arm.interventionNames.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        <Box sx={{ flex: "1 1 45%" }}>
          <Typography variant="h6">Primary Outcomes</Typography>
          {primaryOutcomes && primaryOutcomes.length > 0 ? (
            primaryOutcomes.map((outcome, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{outcome?.measure}:</strong> {outcome?.description}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No primary outcomes available
            </Typography>
          )}
        </Box>
        {secondaryOutcomes?.length > 0 && (
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="h6">Secondary Outcomes</Typography>
            {secondaryOutcomes.map((outcome, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{outcome?.measure}:</strong> {outcome?.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TrialDetails;
